// Centralized, approved OC Tasker business facts.
// Every local-SEO landing page pulls verified claims from here. If a field
// is uncertain, leave it null and the page will omit that claim rather
// than invent one.
//
// Live review count/rating is intentionally NOT stored here — that comes
// from `useReviewStats()` which fetches /api/google-reviews/public and
// keeps the number fresh across the site.
//
// NOTE ON LOCATION LANGUAGE: OC Tasker is a service-area business
// serving Newport Beach and coastal Orange County. It does NOT operate a
// customer-facing storefront and the admin/home office address is
// intentionally hidden from the public. Never claim we are "based in"
// or "located in" any specific city, and never publish the home
// office address. Approved copy is in `serviceAreaStatement` below.
export const OCTASKER_BUSINESS = {
  legalName: "OC Tasker",
  displayName: "OC Tasker",
  phoneDisplay: "(949) 204-0301",
  phoneTel: "+19492040301",
  email: "info@octasker.com",
  domain: "https://octasker.com",
  primaryCity: "Newport Beach",
  primaryState: "CA",
  primaryZip: "92660",

  // Approved factual claims (verified with the business owner)
  fullyInsured: true,
  insuranceStatement: "Fully insured for your peace of mind.",
  workmanshipGuaranteeYears: 1,
  workmanshipGuaranteeStatement:
    "Every job is backed by our 1-year workmanship guarantee. If something isn't right, we come back and make it right.",
  minimumJobPrice: 299,
  minimumJobStatement: "Service visits start at $299 · individual tasks from $99 within your visit.",
  responsePromise:
    "Most estimates answered within 30 minutes during business hours.",
  schedulingPromise:
    "Same-day and next-day service often available for Orange County customers.",

  // Approved service-area language — used anywhere we need to describe
  // OC Tasker's geography. Never say "based in Newport" / "team lives
  // in Newport" — we are a locally-owned Orange County service-area
  // business, not a storefront.
  serviceAreaStatement:
    "OC Tasker is a locally owned Orange County handyman company serving Newport Beach, Corona del Mar, Newport Coast, and surrounding coastal communities.",
  serviceAreaStatementShort:
    "Locally owned Orange County handyman company serving Newport Beach and surrounding coastal communities.",

  // Ranking claim template — city name is the variable. This lets us render
  // "OC Tasker is Newport Beach's #1 rated handyman service" without
  // hard-coding a single city.
  rankingClaim: (cityName) =>
    `OC Tasker is a locally owned handyman service serving ${cityName} and the rest of Orange County`,

  // Single source of truth for business hours. Every consumer (Footer,
  // IntakeForm, SEO/JSON-LD, admin AI agent, email templates) reads
  // from here. Backend has an equivalent constant in
  // `/app/backend/business_hours.py` — keep the two in sync.
  hours: {
    // Display strings
    weekday: "Mon–Fri: 8:00 AM – 6:00 PM",
    saturday: "Sat: 9:00 AM – 4:00 PM",
    sunday: "Sun: Closed",
    // Short forms for tight UI (Footer, badges, SMS auto-reply hints)
    weekdayShort: "Mon-Fri 8am-6pm",
    saturdayShort: "Sat 9am-4pm",
    sundayShort: "Sun closed",
    // Compact single line for meta descriptions + AI SMS agent
    compactLabel: "Mon-Fri 8am-6pm, Sat 9am-4pm, Sun closed",
    // schema.org OpeningHoursSpecification — consumed by SEO.jsx +
    // public/index.html JSON-LD injector.
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "18:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "09:00",
        closes: "16:00",
      },
    ],
  },

  // Approved trust bullets — reusable in the "Why Choose OC Tasker" block.
  whyChoose: [
    { title: "Professional, uniformed technicians",
      desc: "Every OC Tasker technician arrives in uniform, on time, and ready to work." },
    { title: "Fully insured",
      desc: "All work is covered by full business liability insurance." },
    { title: "Clean, precise workmanship",
      desc: "We treat your home like our own — drop cloths, tidy work zones, and no mess left behind." },
    { title: "Clear project communication",
      desc: "Photos, updates, and a written scope so you always know what's happening and what it costs." },
    { title: "1-year workmanship guarantee",
      desc: "If a completed repair fails from our workmanship in the first year, we make it right at no additional cost." },
    { title: "5-star customer feedback",
      desc: "Hundreds of verified 5-star reviews from Orange County homeowners, property managers, and realtors." },
    { title: "Residential and commercial",
      desc: "We serve single-family homes, HOA properties, offices, retail spaces, and property-management portfolios." },
    { title: "Local Orange County knowledge",
      desc: "Serving Newport Beach and surrounding coastal communities — we know the wear patterns, HOA norms, and building styles across OC." },
  ],

  // Approved process — matches what actually happens today.
  process: [
    { step: 1, title: "Send us the details",
      desc: "Submit photos and a short description through the online form, or call us directly. The more detail, the faster we can quote." },
    { step: 2, title: "We review and estimate",
      desc: "A real OC Tasker team member reviews your request — no bots, no bait pricing. You get a clear written estimate or a next-step recommendation." },
    { step: 3, title: "Schedule the work",
      desc: "Pick a time that works for you. Same-day and next-day appointments are often available." },
    { step: 4, title: "We complete the job",
      desc: "Uniformed technicians show up on time with the right tools, materials, and drop cloths. You get progress photos and updates while we work." },
    { step: 5, title: "Final walkthrough",
      desc: "Nothing is finished until you sign off. If something needs adjusting, we handle it before we leave." },
  ],
};

export default OCTASKER_BUSINESS;
